/**
 * SaDrawer — Navigazione SuperAdmin / Consulente nella variant ristorazione.
 *
 * Comportamento responsive (R52.17):
 *  • Mobile (<lg)  → drawer Sheet aperto da FAB in basso a sinistra.
 *  • Desktop (≥lg) → **sidebar fissa** 256px sempre visibile a sinistra.
 *                    Il content principale viene shiftato via `lg:pl-64` su
 *                    `LayoutRistorazione` (gestito lì, solo se SA/Consulente).
 *
 * Nella variant ristorazione non c'è sidebar globale per gli operatori
 * (mobile-first), ma SA/Consulenti hanno bisogno di accedere alle pagine
 * di direzione (Aziende, Lead, Funnel, Analytics, ecc.). Su PC vogliamo
 * un'esperienza vera da pannello (no FAB, no clic per aprire).
 */
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Menu, LayoutDashboard, Building2, TrendingUp, Users as UsersIcon, Activity,
  Calendar, MessageSquare, BarChart3, Briefcase, LifeBuoy, FileText,
  Settings, ShieldCheck, Inbox, ChevronRight, Home, LogOut, FolderDown, Wallet,
  FileSignature, Presentation,
} from "lucide-react";

// R60.50 · Modalità Direttore: quando attiva, nasconde le voci `technical: true`
// (Materiali PDF, Impostazioni, Codici promo, Ticket, Status, Meta Leads) per
// una vista client-facing quando Sara è in visita.
const DIRETTORE_KEY = "sagi:sa:modalita_direttore";

// Voci SA — R60.59 · scope ridotto: SaaS puro, zero CRM/vendita manuale.
// Il cliente arriva solo dalla landing (checkout Stripe auto). SA vede solo
// gestione clienti attivi, legale, report, supporto.
const SA_GROUPS = [
  {
    label: "Gestione",
    items: [
      { to: "/aziende",          label: "Aziende",           icon: Building2 },
      { to: "/contratti",        label: "Contratti firmati", icon: FileSignature },
      { to: "/assistenza-admin", label: "Ticket assistenza", icon: Briefcase },
    ],
  },
  {
    label: "Analisi",
    items: [
      { to: "/report", label: "Report HACCP",       icon: FileText },
      { to: "/status", label: "Status piattaforma", icon: Activity, technical: true },
    ],
  },
  {
    label: "Strumenti",
    items: [
      { to: "/comunicazioni",  label: "Comunicazioni", icon: MessageSquare },
      { to: "/impostazioni-sa", label: "Impostazioni",  icon: Settings, technical: true },
    ],
  },
];

// R60.59 — Il ruolo Consulente è stato rimosso. Mantengo solo per retrocompat:
// se un utente ha ancora role=consultant vede lo stesso menu SA (readonly subset).
const CONSULENTE_GROUPS = SA_GROUPS;

/** Contenuto navigazione condiviso fra mobile drawer e sidebar desktop. */
function NavContent({ groups, isSa, userLabel, userSub, onItemClick, pathname, idPrefix = "sa", onLogout, direttore, setDirettore }) {
  // R60.50 · Filtra voci `technical` quando Modalità Direttore è ON
  const visibleGroups = groups
    .map((g) => ({ ...g, items: direttore ? g.items.filter((i) => !i.technical) : g.items }))
    .filter((g) => g.items.length > 0);
  return (
    <>
      <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-5 py-4 shrink-0">
        <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
          {isSa ? "Sa.Gi Group · Direzione" : "Sa.Gi Group · Consulente"}
        </p>
        <h2 className="text-lg font-bold mt-0.5 truncate">{userLabel}</h2>
        <p className="text-[11px] text-white/70 mt-0.5">{userSub}</p>
        {/* R60.50 · Modalità Direttore toggle (solo SA) */}
        {isSa && (
          <button
            type="button"
            onClick={() => setDirettore(!direttore)}
            className="mt-3 flex items-center gap-2 group"
            data-testid={`${idPrefix}-toggle-direttore`}
          >
            <span className={`relative inline-flex w-9 h-5 rounded-full transition ${direttore ? "bg-amber-400" : "bg-white/20"}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${direttore ? "translate-x-4" : ""}`} />
            </span>
            <span className="flex items-center gap-1 text-[11px] text-white/80 group-hover:text-white transition">
              <Presentation className="w-3 h-3 text-amber-300" /> Modalità direttore
            </span>
          </button>
        )}
      </div>

      <nav className="py-3 flex-1 overflow-y-auto">
        {visibleGroups.map((g) => (
          <div key={g.label} className="mb-3">
            <p className="px-5 mb-1 text-[10px] uppercase tracking-widest font-bold text-slate-500">{g.label}</p>
            <ul>
              {g.items.map((it) => {
                const active = pathname === it.to;
                return (
                  <li key={it.to}>
                    <Link
                      to={it.to}
                      onClick={onItemClick}
                      className={`flex items-center gap-3 px-5 py-2.5 text-sm transition ${
                        active ? "bg-[#13294B] text-white font-semibold" : "text-slate-700 hover:bg-slate-100"
                      }`}
                      data-testid={`${idPrefix}-link-${it.to.replace(/\//g, "")}`}
                    >
                      <it.icon className={`w-4 h-4 shrink-0 ${active ? "text-amber-300" : "text-slate-400"}`} />
                      <span className="flex-1 truncate">{it.label}</span>
                      {active && <ChevronRight className="w-3 h-3 text-amber-300" />}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {direttore && (
          <div className="mx-5 mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[10px] text-amber-800">
            <Presentation className="w-3 h-3 inline mr-1" />
            Modalità direttore attiva &middot; voci tecniche nascoste
          </div>
        )}
        <div className="px-5 py-3 border-t border-slate-200 mt-2 space-y-2">
          <Link
            to="/"
            onClick={onItemClick}
            className="text-[11px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
            data-testid={`${idPrefix}-link-home`}
          >
            <Home className="w-3 h-3" /> Home Ristorazione
          </Link>
          <button
            type="button"
            onClick={onLogout}
            className="w-full mt-2 h-10 rounded-lg bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-[13px] font-semibold inline-flex items-center justify-center gap-2 active:scale-[.99] transition"
            data-testid={`${idPrefix}-logout`}
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </nav>
    </>
  );
}

export default function SaDrawer() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  // R60.50 · Modalità Direttore (persisted in localStorage)
  const [direttore, setDirettoreState] = useState(() => {
    try { return localStorage.getItem(DIRETTORE_KEY) === "1"; } catch { return false; }
  });
  const setDirettore = (v) => {
    setDirettoreState(v);
    try { localStorage.setItem(DIRETTORE_KEY, v ? "1" : "0"); } catch {}
  };

  const role = user?.role;
  if (role !== "superadmin" && role !== "consultant") return null;

  const groups = role === "superadmin" ? SA_GROUPS : CONSULENTE_GROUPS;
  const isSa = role === "superadmin";
  const userLabel = user?.name || user?.email || "Utente";
  const userSub = isSa ? "SuperAdmin · accesso completo" : "Consulente HACCP";

  const handleLogout = async () => {
    setOpen(false);
    try { await logout(); } catch { /* noop */ }
    nav("/login", { replace: true });
  };

  return (
    <>
      {/* ─── MOBILE: FAB + Sheet (visibile solo < lg) ─────────────────── */}
      <div className="lg:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="Apri menu direzione"
              data-testid="sa-drawer-open"
              className="sr-only"
            >
              <Menu className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-full sm:w-80 sm:max-w-[85vw] flex flex-col" data-testid="sa-drawer-content">
            <SheetTitle className="sr-only">Menu direzione</SheetTitle>
            <SheetDescription className="sr-only">
              Navigazione per ruoli direzionali (SuperAdmin / Consulente) nella variant ristorazione.
            </SheetDescription>
            <NavContent
              groups={groups}
              isSa={isSa}
              userLabel={userLabel}
              userSub={userSub}
              onItemClick={() => setOpen(false)}
              pathname={loc.pathname}
              idPrefix="sa"
              onLogout={handleLogout}
              direttore={direttore}
              setDirettore={setDirettore}
            />
          </SheetContent>
        </Sheet>
      </div>

      {/* ─── DESKTOP: sidebar fissa permanente (visibile solo ≥ lg) ──── */}
      <aside
        className="hidden lg:flex fixed top-0 left-0 bottom-0 w-64 flex-col bg-white border-r border-slate-200 shadow-sm z-40"
        data-testid="sa-sidebar-desktop"
      >
        <NavContent
          groups={groups}
          isSa={isSa}
          userLabel={userLabel}
          userSub={userSub}
          pathname={loc.pathname}
          idPrefix="sa-side"
          onLogout={handleLogout}
          direttore={direttore}
          setDirettore={setDirettore}
        />
      </aside>
    </>
  );
}
